import Theme from "../../../../../utils/Theme";

// Short-height landscape phones (rotate-to-landscape flows like the Warranty
// module). Applied here at the shared style level so EVERY artifact screen
// (survey, appointment, review, warranty, trade, etc.) shrinks its outer
// chrome together, instead of each response component needing its own copy
// of this breakpoint just to fit in whatever space mainBox/contentBox leave.
const LANDSCAPE_SHORT = "@media (orientation: landscape) and (max-height: 500px)";

export const ArtifactStyle = {
  // Outer wrapper for the whole artifact screen (toggle + content + pagination)
  mainBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    alignItems: "center",
    gap: "2%",
    p: "3%",
    [LANDSCAPE_SHORT]: {
      gap: "1%",
      p: "1.5%",
    },
  },

  // ── ZONE 1 — Toggle bar (language/mode dropdowns + dealer logo) ──────────
  // Sized by its own content. Never grows, never shrinks below content size.
  toggleZone: {
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },

  // ── ZONE 2 — Video / content area ─────────────────────────────────────
  // Takes all remaining space after Zone 1 + Zone 3 are sized.
  // minHeight: 0 is what lets this shrink on short/landscape screens
  // instead of overflowing its parent.
  contentBox: {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    position: "relative",
  },

  // Overlay shown at end of AI avatar videos (survey, referral, rating, etc.)
  videoResponseOverlay: {
    position: "absolute",
    bottom: { xs: 56, sm: 60, md: 70 },
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    gap: 1,
    alignItems: "center",
    justifyContent: "flex-end",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    px: { xs: 1, sm: 1.5 },
     maxHeight: { xs: "80%", sm: "78%", md: "78%" },
    // overflowX: "hidden",
    // overflowY: "auto",
    pointerEvents: "auto",
    transition: "opacity 0.6s ease",
    [LANDSCAPE_SHORT]: {
      bottom: 8,
      maxHeight: "92%",
      gap: 0.5,
    },
  },

  // ── ZONE 3 — Pagination / nav ─────────────────────────────────────────
  // Sized by content, pinned to the bottom of mainBox by flex ordering.
  // Safe-area padding keeps it clear of iOS home indicator / Android
  // gesture bar / mobile browser chrome.
  paginationZone: {
    flex: "0 0 auto",
    width: "100%",
    pb: "max(4px, env(safe-area-inset-bottom))",
    [LANDSCAPE_SHORT]: {
      pb: "max(2px, env(safe-area-inset-bottom))",
    },
  },

  logo: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
    flex: "0 0 auto",
    alignItems: "center",
  },
  artifactOutsideBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
    height: "90%",
    alignItems: "center",
  },
  crossIcon: {
    alignSelf: "end",
    marginRight: "30px",
    color: Theme.palette.error.main,
    cursor: "pointer",
    fontSize: "35px",
  },
  insideBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
    alignItems: "center",
    height: "80%",
  },
  artifactBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    width: "80%",
    alignItems: "center",
    height: "100%",
    overflowY: "scroll",
  },
  textBox: {
    whiteSpace: "pre-line",
    fontFamily: `"Open Sans", "Helvetica", "Roboto", sans-serif`,
    fontSize: "22px",
    
  },
  notext: {
    display: "flex",
    whiteSpace: "pre-line",
    fontFamily: `"Open Sans", "Helvetica", "Roboto", sans-serif`,
    fontSize: "20px",
    alignItems: "center",
    justifyContent: "center",
  },
  videoBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
    minHeight: 0,
  },
  dealerAppOutsideBox: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: "30px", sm: "64px" },
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  dealerAppInsideBox: { display: "flex", flexDirection: "column", gap: "10px" },
  appstoreBox: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: { xs: "100%", sm: "10%" },
    mb: { xs: 2, sm: 0 },
  },
  appstoreButtton: {
    textTransform: "none",
    width: { xs: 200, sm: "100%" },
    fontWeight: "bold",
    bgcolor: "white",
  },
  appointmentBox: {
    display: "flex",
    flexDirection: "column",
  
  gap: {
    xs: 1,
    sm: 2,
    md: 3,
  },

    width: "100%",
  },
  datePicker: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: 3, sm: 2 },
    width: "100%",
  },
  preference: { display: "flex", flexDirection: "row", width: "100%" },
  yesOutBox: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    height: "250px",
    gap: "8px",
    overflowY: "scroll",
  },
  refOutBox: {
    width: "100%",
    height: "100%",
    gap: "8px",
  },
  refInsideBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  textFieldBox: {
    display: "flex",
    width: "100%",
    height: "100%",
    flexDirection: "row",
    gap: { xs: "12px", sm: "20px" },
  },
  formControll: {
    width: "40%",
    minWidth: "200px",
    height: "56px",
  },
  addcircleIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "56px",
    width: "100%",
  },
  artifactButton: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: 5, sm: 1 },
  },
  surveyOutBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    gap: 5,
  },
  optionsBox: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    flexWrap: "wrap",
    gap: 2,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    pl: { xs: 1, sm: 0 },
  },
  submitRef: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  AddNewButton: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    justifyContent: "flex-end",
  },
};